import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, AlertTriangle, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AbuserRecord {
  id: number;
  personId: number;
  agencyId: number;
  firstName: string | null;
  lastName: string | null;
  details: string | null;
  createdAt: string;
  matchType?: "exact" | "phonetic";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AbuserSearch() {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");

  const { data: results, isLoading } = useQuery<{ data: AbuserRecord[] }>({
    queryKey: ["abuserSearch", submittedQuery],
    queryFn: () =>
      api.get<{ data: AbuserRecord[] }>("/abusers/search", {
        params: { q: submittedQuery },
      }),
    enabled: submittedQuery.length >= 2,
  });

  const createAlertMutation = useMutation({
    mutationFn: (data: {
      firstName?: string;
      lastName?: string;
      reason: string;
      severity?: string;
    }) => api.post("/alerts", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["alerts"] });
    },
  });

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (query.trim().length >= 2) {
      setSubmittedQuery(query.trim());
    }
  }

  const abusers = results?.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Abuser Search</h1>
        <p className="text-sm text-muted-foreground">
          Search the abuser database with Soundex phonetic matching.
        </p>
      </div>

      {/* Search form */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name (first, last, or both)..."
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <Button type="submit" className="gap-2" disabled={query.trim().length < 2}>
              <Search className="h-4 w-4" />
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Results */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {!isLoading && submittedQuery && abusers.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No abuser records found for "{submittedQuery}".
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && abusers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Results ({abusers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Match
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Details
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                    Date Added
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {abusers.map((abuser) => (
                  <tr key={abuser.id} className="border-b last:border-0">
                    <td className="px-4 py-3 font-medium">
                      {abuser.firstName} {abuser.lastName}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                          abuser.matchType === "exact"
                            ? "bg-green-100 text-green-700"
                            : "bg-amber-100 text-amber-700",
                        )}
                      >
                        {abuser.matchType === "exact"
                          ? "Exact"
                          : "Phonetic"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {abuser.details
                        ? abuser.details.length > 80
                          ? abuser.details.slice(0, 80) + "..."
                          : abuser.details
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(abuser.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        onClick={() =>
                          createAlertMutation.mutate({
                            firstName: abuser.firstName ?? undefined,
                            lastName: abuser.lastName ?? undefined,
                            reason: `Alert created from search: ${abuser.firstName} ${abuser.lastName}`,
                            severity: "warning",
                          })
                        }
                        loading={createAlertMutation.isPending}
                      >
                        <AlertTriangle className="h-3 w-3" />
                        Create Alert
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
